import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Link,
  Option,
  Select,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { I18N_DE } from '@/i18n/de';
import { EMAIL_PATTERN } from '@/modules/EMAIL_PATTERN';
import { PHONE_NUMBER_PATTERN } from '@/modules/PHONE_NUMBER_PATTERN';
import { UserGuard } from '@/modules/UserGuard';
import { UpdateUserDto, User, UserInstitutionType, UserNotificationInterval } from '@/modules/api';
import { backendApiUsersUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useUserQuery } from '@/queries/useUserQuery';
import { theme } from '@/setup/theme';
import { getApiValidationError } from '@/utils/ErrorHelper';

export const Route = createFileRoute('/_authenticated/_management/profil/edit')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: ProfileRoute,
});

type FormData = Required<
  Pick<
    UpdateUserDto,
    'firstName' | 'lastName' | 'notificationInterval' | 'email' | 'phoneNumber' | 'roleText' | 'institutionType'
  >
>;

export function ProfileRoute() {
  const { data: loginUser } = useUserQuery();
  const user = UserGuard.isUser(loginUser) ? (loginUser as User) : null;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>();

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        roleText: user.roleText,
        institutionType: user.institutionType,
        notificationInterval: user?.notificationInterval || UserNotificationInterval.WEEKDAYS,
      });
    }
  }, [user, reset]);

  const { mutate, error: mutateError, isPending } = useMutation(backendApiUsersUpdateMutation());
  const validationError = getApiValidationError(mutateError);

  return (
    user && (
      <Grid
        container
        spacing={2}
        padding={4}
        alignSelf="center"
        md={8}
        component="form"
        onSubmit={handleSubmit((data) => {
          if (isPending) return;

          mutate({ path: { userId: user.id }, body: data });
        })}
        sx={{ backgroundColor: theme.palette.background.surface }}
      >
        <Grid xs={12}>
          {!user.organization && (
            <Alert color="danger" sx={{ marginBottom: 2 }} variant="outlined">
              <Box>
                <Typography level="title-lg">Keine Orgranisation zugeordnet</Typography>
                <Typography>
                  Ihnen ist keine Organisation zugeorndet. Bei Fragen kontaktieren Sie bitte an den Support{' '}
                  <Link href="mailto:verkehrszeichenkataster@vm.bwl.de">verkehrszeichenkataster@vm.bwl.de</Link>.
                </Typography>
              </Box>
            </Alert>
          )}
          {user.organization && !user.organization.active && (
            <Alert color="danger" sx={{ marginBottom: 2 }} variant="outlined">
              <Box>
                <Typography level="title-lg">Warten auf Freischaltung in Organisation</Typography>
                <Typography>
                  Ihre Freischaltung wird durch Manager*innen ihrer Organisation geprüft. Bei Fragen kontaktieren Sie
                  bitte an den Support{' '}
                  <Link href="mailto:verkehrszeichenkataster@vm.bwl.de">verkehrszeichenkataster@vm.bwl.de</Link>.
                </Typography>
              </Box>
            </Alert>
          )}

          <Typography level="h3" marginBottom={2}>
            Profil
          </Typography>
          <Typography color="neutral">{`Registriert über Service BW mit E-Mail: ${user.registrationEmail}`}</Typography>
        </Grid>

        <Grid xs={6}>
          <FormControl error={!!errors.email}>
            <FormLabel>Dienstliche E-Mail-Adresse</FormLabel>
            <Input {...register('email', { required: true, pattern: EMAIL_PATTERN })} variant="outlined" />
            {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={6}>
          <FormControl error={!!errors.phoneNumber}>
            <FormLabel>Telefonnummer</FormLabel>
            <Input
              {...register('phoneNumber', { required: true, pattern: PHONE_NUMBER_PATTERN })}
              type="tel"
              variant="outlined"
              placeholder="+4915112345678"
            />
            {errors.phoneNumber && <FormHelperText>{errors.phoneNumber.message}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={6}>
          <FormControl error={!!errors.firstName}>
            <FormLabel>Vorname</FormLabel>
            <Input {...register('firstName', { required: true })} variant="outlined" />
            {errors.firstName && <FormHelperText>Pflichtfeld</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={6}>
          <FormControl error={!!errors.lastName}>
            <FormLabel>Nachname</FormLabel>
            <Input {...register('lastName', { required: true })} variant="outlined" />
            {errors.lastName && <FormHelperText>Pflichtfeld</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={6}>
          <FormControl error={!!errors.institutionType}>
            <FormLabel>Institution</FormLabel>
            <Controller
              control={control}
              name="institutionType"
              defaultValue={user.institutionType}
              render={({ field }) => (
                <Select {...field} onChange={(event, value) => field.onChange(value)} variant="outlined">
                  {Object.values(UserInstitutionType).map((option) => (
                    <Option key={option} value={option}>
                      {I18N_DE.UserInstitutionType[option]}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.institutionType && <FormHelperText>Pflichtfeld</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={6}>
          <FormControl error={!!errors.roleText}>
            <FormLabel>Funktion</FormLabel>
            <Input {...register('roleText', { required: true })} variant="outlined" />
            {errors.roleText && <FormHelperText>Pflichtfeld</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <FormControl>
            <FormLabel>Benachrichtigungsintervall</FormLabel>
            <Controller
              control={control}
              name="notificationInterval"
              defaultValue={user?.notificationInterval || UserNotificationInterval.WEEKDAYS}
              render={({ field }) => (
                <Select {...field} onChange={(event, value) => field.onChange(value)} variant="outlined">
                  {Object.values(UserNotificationInterval).map((userNotificationInterval) => (
                    <Option key={userNotificationInterval} value={userNotificationInterval}>
                      {I18N_DE.UserNotificationInterval[userNotificationInterval]}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <Button type="submit" disabled={!isDirty || isPending} loading={isPending}>
            Speichern
          </Button>
          {mutateError && (
            <Alert style={{ marginTop: 10 }} color="danger">
              {`Anfrage fehlgeschlagen. ${validationError?.message ?? ''}`}
            </Alert>
          )}
        </Grid>
      </Grid>
    )
  );
}
